'use client'

import { useEffect, useState } from 'react'

// Breakpoints y gutters del grid del sitio (`PageContainer`/`_mixins.scss`
// `breakpoint`): $tablet 768px (gutter 24px), $desktop 1024px (gutter 40px,
// mismo gutter hasta el tope de ancho de contenido 2100px). Fijos para
// cualquier grid del sitio — no varían de una página a otra, así que (a
// diferencia de `estimateRowHeight` más abajo) no hace falta inyectarlos por
// config.
const TABLET_MIN_WIDTH = 768
const DESKTOP_MIN_WIDTH = 1024
const DESKTOP_MEDIUM_MIN_WIDTH = 1200
const MAX_CONTENT_WIDTH = 2100

const GUTTER_MOBILE = 16
const GUTTER_TABLET = 24
const GUTTER_DESKTOP = 40

const DEFAULT_VIEWPORT_WIDTH = 375 // fallback para el primer render en servidor

export interface ResponsiveGridConfig {
  readonly columns: number
  readonly estimatedRowHeight: number
}

/** Umbral desktop compartido con `useIsDesktopViewport` (mismo breakpoint que
 * usa este hook para las columnas) — exportado para que el selector de vista
 * horizontal/vertical de las páginas consumidoras use exactamente el mismo
 * corte, sin duplicar el número mágico 1024. */
export { DESKTOP_MIN_WIDTH }

export interface UseResponsiveColumnsConfig {
  /** Separación entre columnas del grid, en px (entra en el cálculo del ancho
   * de columna real). Sin valor por defecto compartido — cada página usa su
   * propio `COLUMN_GAP` (mismo motivo que `pageSize`/`threshold` en
   * `useInfiniteList`: no hay un valor común a todas las integraciones). */
  readonly columnGapPx: number
  /** Alto estimado (px) de una fila a partir del ancho de columna real del
   * viewport actual. Cada página lo deriva de su propio diseño de card
   * (aspect ratio de imagen, alto de cuerpo/CTA...) — sin valor por defecto
   * compartido, por el mismo motivo que `columnGapPx`. Debe ser una función
   * estable entre renders (definida a nivel de módulo o memoizada), o el
   * efecto de resize de este hook se reengancha en cada render. */
  readonly estimateRowHeight: (columnWidth: number) => number
  /**
   * Fuerza un número de columnas (en vez del cálculo responsive por defecto
   * de la cuadrícula vertical), como número fijo o como función del ancho de
   * viewport actual (p. ej. una vista de lista con cards horizontales: 2
   * columnas en desktop, 1 en mobile/tablet — ver `computeHorizontalColumns`
   * más abajo). El ancho de columna real (el que recibe `estimateRowHeight`)
   * se sigue derivando del viewport actual con ese número de columnas
   * (specs/019-estados-compra-eventos).
   */
  readonly columnsOverride?: number | ((viewportWidth: number) => number)
}

/**
 * Columnas para la vista horizontal/lista (cards `HorizontalCard`): 2 en
 * desktop (≥1024px), 1 en mobile/tablet — a diferencia de la cuadrícula
 * vertical (`computeColumns`), no escala en más pasos porque cada card ya
 * ocupa una fila ancha por sí sola.
 */
export function computeHorizontalColumns(viewportWidth: number): number {
  return viewportWidth >= DESKTOP_MIN_WIDTH ? 2 : 1
}

function computeColumns(viewportWidth: number): number {
  // 5 columnas solo a partir de $desktop-medium (1200px); en todo el rango
  // $desktop (1024–1199px), 4; en $tablet (768–1023px), 3; por debajo de
  // $tablet, 2 (antes 5 ya desde 1024px, y 3/2 repartidas en dos tramos más
  // por debajo de $tablet).
  return viewportWidth >= DESKTOP_MEDIUM_MIN_WIDTH
    ? 5
    : viewportWidth >= DESKTOP_MIN_WIDTH
      ? 4
      : viewportWidth >= TABLET_MIN_WIDTH
        ? 3
        : 2
}

function computeColumnWidth(viewportWidth: number, columns: number, columnGapPx: number): number {
  const gutter =
    viewportWidth >= DESKTOP_MIN_WIDTH ? GUTTER_DESKTOP : viewportWidth >= TABLET_MIN_WIDTH ? GUTTER_TABLET : GUTTER_MOBILE
  const contentWidth = Math.min(viewportWidth, MAX_CONTENT_WIDTH)
  const usableWidth = Math.max(0, contentWidth - gutter * 2)
  const totalGaps = columnGapPx * (columns - 1)
  return Math.max(0, (usableWidth - totalGaps) / columns)
}

function resolveColumnsOverride(
  viewportWidth: number,
  columnsOverride: number | ((viewportWidth: number) => number) | undefined,
): number | undefined {
  return typeof columnsOverride === 'function' ? columnsOverride(viewportWidth) : columnsOverride
}

function computeConfig(
  viewportWidth: number,
  columnGapPx: number,
  estimateRowHeight: (columnWidth: number) => number,
  columnsOverride?: number | ((viewportWidth: number) => number),
): ResponsiveGridConfig {
  const columns = resolveColumnsOverride(viewportWidth, columnsOverride) ?? computeColumns(viewportWidth)
  const columnWidth = computeColumnWidth(viewportWidth, columns, columnGapPx)
  return { columns, estimatedRowHeight: estimateRowHeight(columnWidth) }
}

/**
 * Configuración de grid (columnas + alto de fila estimado) para el viewport
 * actual — genérico y reutilizable por cualquier grid responsive del sitio,
 * no solo el de una página concreta (ver contracts/infinite-list-hook.md
 * para el mismo principio aplicado a `useInfiniteList`: el hook no conoce la
 * entidad ni el diseño de card, solo la config que le inyecta cada página).
 *
 * Arranca siempre con `DEFAULT_VIEWPORT_WIDTH` en servidor y en el primer
 * render de cliente (antes de hidratar, `window` ya existe pero usarlo aquí
 * produciría un resultado distinto al HTML ya renderizado por el servidor —
 * hidratación rota). El viewport real se aplica después, en el efecto de
 * abajo (solo cliente, tras montar).
 */
export function useResponsiveColumns({
  columnGapPx,
  estimateRowHeight,
  columnsOverride,
}: UseResponsiveColumnsConfig): ResponsiveGridConfig {
  const [config, setConfig] = useState<ResponsiveGridConfig>(() =>
    computeConfig(DEFAULT_VIEWPORT_WIDTH, columnGapPx, estimateRowHeight, columnsOverride),
  )

  useEffect(() => {
    let ticking = false
    function update() {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        setConfig(computeConfig(window.innerWidth, columnGapPx, estimateRowHeight, columnsOverride))
        ticking = false
      })
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [columnGapPx, estimateRowHeight, columnsOverride])

  return config
}
